import { Skeleton } from "@/components/ui/skeleton";
import type {
  AgentPulseDataSnapshot,
  ViewsSummaryResponse,
} from "@forgepoint/shared";

interface SentQuoteViewsProps {
  viewsSummary: ViewsSummaryResponse | null;
  pulseSnapshot: AgentPulseDataSnapshot | null;
  isLoading: boolean;
}

/**
 * Displays a summary of views on SENT quotes — total views this week,
 * a weekly mini-bar chart, and top viewing cities, with deltas compared
 * against the last pulse snapshot.
 * @param viewsSummary - Live views summary data
 * @param pulseSnapshot - Snapshot data frozen at pulse generation time
 * @param isLoading - Whether data is still being fetched
 * @returns A compact card showing quote view engagement
 */
export function SentQuoteViews({
  viewsSummary,
  pulseSnapshot,
  isLoading,
}: SentQuoteViewsProps) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Quote Views
        </h3>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : !viewsSummary ? (
        <EmptyState />
      ) : (
        <ViewsContent
          viewsSummary={viewsSummary}
          pulseSnapshot={pulseSnapshot}
        />
      )}
    </div>
  );
}

/**
 * Renders the views content: total this week, weekly bar chart, and top cities.
 * Uses the pulse snapshot for delta comparison when available.
 * @param viewsSummary - The views summary response
 * @param pulseSnapshot - Snapshot data frozen at pulse generation time
 * @returns Views stats and chart
 */
function ViewsContent({
  viewsSummary,
  pulseSnapshot,
}: {
  viewsSummary: ViewsSummaryResponse;
  pulseSnapshot: AgentPulseDataSnapshot | null;
}) {
  const { viewsPerWeek, topCities } = viewsSummary;

  const thisWeekViews =
    viewsPerWeek.length > 0 ? viewsPerWeek[viewsPerWeek.length - 1].count : 0;
  const totalViews = viewsPerWeek.reduce((sum, w) => sum + w.count, 0);

  const hasSnapshot = pulseSnapshot?.quoteViewsThisWeek !== undefined;
  const totalDelta =
    pulseSnapshot?.quoteViewsTotal !== undefined
      ? totalViews - pulseSnapshot.quoteViewsTotal
      : null;

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-lg border bg-card px-3.5 py-3">
          <span className="text-xl font-semibold tabular-nums">
            {thisWeekViews}
          </span>
          <p className="mt-0.5 text-xs font-medium text-foreground/80">
            This week
          </p>
          {hasSnapshot && (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">
              {pulseSnapshot?.quoteViewsThisWeek ?? 0} last week
            </p>
          )}
        </div>
        <div className="flex-1 rounded-lg border bg-card px-3.5 py-3">
          <span className="text-xl font-semibold tabular-nums">
            {totalViews}
          </span>
          <p className="mt-0.5 text-xs font-medium text-foreground/80">
            Last 12 weeks
          </p>
          {totalDelta !== null && totalDelta !== 0 && (
            <p
              className={`mt-1 font-mono text-[10px] ${
                totalDelta > 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {totalDelta > 0 ? "▲" : "▼"} {Math.abs(totalDelta)} since last
              pulse
            </p>
          )}
        </div>
      </div>

      {/* Top cities */}
      {topCities.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Top cities
          </p>
          <div className="space-y-1">
            {topCities.map((city) => (
              <div
                key={city.city}
                className="flex items-center justify-between text-xs"
              >
                <span className="text-foreground/80">{city.city}</span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {city.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border bg-card px-4 py-10 text-center">
      <p className="text-sm text-muted-foreground">No quote views yet</p>
      <p className="mt-1 text-xs text-muted-foreground/70">
        Views will appear here once customers open sent quotes.
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div className="flex-1 rounded-lg border bg-card px-3.5 py-3">
          <Skeleton className="h-6 w-10" />
          <Skeleton className="mt-1.5 h-3 w-16" />
        </div>
        <div className="flex-1 rounded-lg border bg-card px-3.5 py-3">
          <Skeleton className="h-6 w-10" />
          <Skeleton className="mt-1.5 h-3 w-16" />
        </div>
      </div>
      <Skeleton className="h-12 w-full" />
    </div>
  );
}
